import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { ContactShadows, Environment } from "@react-three/drei";
import * as THREE from "three";
import gsap from "gsap";
import { Clay, type ClayHandle } from "./Clay";
import { Wheel } from "./Wheel";
import { RingCursor, type RingCursorHandle } from "./RingCursor";
import { createDispEngine, type DispEngine } from "./displacementCanvas";
import {
  BG_COLOR,
  CAMERA_Z_MAX,
  CAMERA_Z_MIN,
  DISP_H_DESKTOP,
  DISP_H_MOBILE,
  DISP_W_DESKTOP,
  DISP_W_MOBILE,
  HISTORY_MAX,
  type ToolId,
} from "./constants";
import { usePrefersReducedMotion, useIsMobile } from "./useIsMobile";

interface StudioProps {
  clayColor: string;
  onColorFlashRequest: (fn: () => void) => void;
  speedRef: React.MutableRefObject<number>;
  tool: ToolId;
  brushSize: number;
  onReadyForReset: (fn: () => void) => void;
  onReadyForUndo: (fn: () => void) => void;
  cameraZoomTargetRef: React.MutableRefObject<number>;
  announce: (msg: string) => void;
}

/**
 * Full Studio scene. Owns the displacement engine, raycasting, camera dolly, and stroke history.
 */
export function Studio(props: StudioProps) {
  const isMobile = useIsMobile();
  const reducedMotion = usePrefersReducedMotion();
  const [dispEngine, setDispEngine] = useState<DispEngine | null>(null);

  useEffect(() => {
    const w = isMobile ? DISP_W_MOBILE : DISP_W_DESKTOP;
    const h = isMobile ? DISP_H_MOBILE : DISP_H_DESKTOP;
    const engine = createDispEngine(w, h);
    setDispEngine(engine);
    return () => {
      engine.dispose();
    };
  }, [isMobile]);

  if (!dispEngine) return null;

  return (
    <Canvas
      className="pottery-canvas-area"
      shadows
      dpr={[1, 2]}
      gl={{
        antialias: true,
        alpha: false,
        powerPreference: "high-performance",
        toneMapping: THREE.ACESFilmicToneMapping,
      }}
      camera={{ position: [6, 3.2, 18], fov: 45, near: 0.1, far: 200 }}
      onCreated={(state) => {
        state.gl.setClearColor(new THREE.Color(BG_COLOR));
        state.gl.toneMappingExposure = 1.0;
      }}
    >
      <Suspense fallback={null}>
        <SceneInner
          dispEngine={dispEngine}
          isMobile={isMobile}
          reducedMotion={reducedMotion}
          {...props}
        />
      </Suspense>
    </Canvas>
  );
}

interface SceneInnerProps extends StudioProps {
  dispEngine: DispEngine;
  isMobile: boolean;
  reducedMotion: boolean;
}

function SceneInner({
  dispEngine,
  isMobile,
  reducedMotion,
  clayColor,
  onColorFlashRequest,
  speedRef,
  tool,
  brushSize,
  onReadyForReset,
  onReadyForUndo,
  cameraZoomTargetRef,
  announce,
}: SceneInnerProps) {
  const { camera, gl, scene, size } = useThree();
  const clayRef = useRef<ClayHandle>(null);
  const cursorRef = useRef<RingCursorHandle>(null);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const pointerNdc = useRef(new THREE.Vector2(0, 0));
  const isPointerInside = useRef(false);
  const isDragging = useRef(false);
  const historyRef = useRef<ImageData[]>([]);
  const strokeStartSnapRef = useRef<ImageData | null>(null);
  const toolRef = useRef<ToolId>(tool);
  const brushRef = useRef<number>(brushSize);
  const lastPaintTime = useRef<number>(0);
  const lastPaintUvRef = useRef<{ u: number; v: number } | null>(null);

  useEffect(() => {
    toolRef.current = tool;
  }, [tool]);
  useEffect(() => {
    brushRef.current = brushSize;
  }, [brushSize]);

  // Expose flash to parent
  useEffect(() => {
    onColorFlashRequest(() => clayRef.current?.flash());
  }, [onColorFlashRequest]);

  // Reset & undo hooks
  useEffect(() => {
    onReadyForReset(() => {
      if (reducedMotion) {
        dispEngine.reset();
        historyRef.current = [];
        announce("Clay reset.");
        return;
      }
      // Animate reset by lerping every pixel back toward gray.
      const startSnap = dispEngine.snapshot();
      const w = dispEngine.width;
      const h = dispEngine.height;
      const total = w * h * 4;
      const proxy = { t: 0 };
      gsap.to(proxy, {
        t: 1,
        duration: 0.6,
        ease: "power2.inOut",
        onUpdate: () => {
          const cur = dispEngine.ctx.createImageData(w, h);
          for (let i = 0; i < total; i += 4) {
            const src = startSnap.data[i];
            cur.data[i] = src + (128 - src) * proxy.t;
            cur.data[i + 1] = cur.data[i];
            cur.data[i + 2] = cur.data[i];
            cur.data[i + 3] = 255;
          }
          dispEngine.ctx.putImageData(cur, 0, 0);
          dispEngine.texture.needsUpdate = true;
        },
        onComplete: () => {
          dispEngine.reset();
          historyRef.current = [];
          announce("Clay reset.");
        },
      });
    });

    onReadyForUndo(() => {
      const snap = historyRef.current.pop();
      if (snap) {
        dispEngine.restore(snap);
        announce("Undo last stroke.");
      }
    });
  }, [dispEngine, onReadyForReset, onReadyForUndo, reducedMotion, announce]);

  // Pointer handlers on the canvas DOM.
  useEffect(() => {
    const el = gl.domElement;
    const setNdc = (clientX: number, clientY: number) => {
      const rect = el.getBoundingClientRect();
      pointerNdc.current.set(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -(((clientY - rect.top) / rect.height) * 2 - 1),
      );
    };
    const onPointerMove = (e: PointerEvent) => {
      setNdc(e.clientX, e.clientY);
      isPointerInside.current = true;
    };
    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0 && e.pointerType === "mouse") return;
      setNdc(e.clientX, e.clientY);
      // Only start stroke if we're hitting the clay
      if (!clayRef.current?.mesh) return;
      raycaster.setFromCamera(pointerNdc.current, camera);
      const hit = raycaster.intersectObject(clayRef.current.mesh, false)[0];
      if (!hit) return;
      isDragging.current = true;
      strokeStartSnapRef.current = dispEngine.snapshot();
      lastPaintUvRef.current = null; // fresh stroke
      cursorRef.current?.setActive(true);
      el.setPointerCapture(e.pointerId);
    };
    const onPointerUp = (e: PointerEvent) => {
      if (!isDragging.current) return;
      isDragging.current = false;
      lastPaintUvRef.current = null;
      cursorRef.current?.setActive(false);
      // Commit stroke snapshot
      if (strokeStartSnapRef.current) {
        historyRef.current.push(strokeStartSnapRef.current);
        if (historyRef.current.length > HISTORY_MAX) historyRef.current.shift();
        strokeStartSnapRef.current = null;
      }
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
    };
    const onPointerLeave = () => {
      isPointerInside.current = false;
      cursorRef.current?.hide();
    };
    // Scroll wheel dolly
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const range = CAMERA_Z_MAX - CAMERA_Z_MIN;
      const scale = range / 500;
      cameraZoomTargetRef.current = THREE.MathUtils.clamp(
        cameraZoomTargetRef.current + e.deltaY * scale * 0.5,
        CAMERA_Z_MIN,
        CAMERA_Z_MAX,
      );
    };

    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointerleave", onPointerLeave);
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointerleave", onPointerLeave);
      el.removeEventListener("wheel", onWheel);
    };
  }, [gl, camera, raycaster, dispEngine, cameraZoomTargetRef]);

  // Camera dolly + raycasting each frame
  useFrame((_state, deltaRaw) => {
    const delta = Math.min(deltaRaw, 0.1);
    // Dolly camera along Z (relative to origin direction) — but keep look-at at origin.
    const target = cameraZoomTargetRef.current;
    const damping = reducedMotion ? 1 : 0.08;
    const cur = camera.position.length();
    const nextLen = cur + (target - cur) * damping;
    camera.position.setLength(nextLen);
    camera.lookAt(0, 1.5, 0);

    // Update ring cursor / sculpt.
    if (!clayRef.current?.mesh) return;
    if (!isPointerInside.current) {
      cursorRef.current?.hide();
      return;
    }
    raycaster.setFromCamera(pointerNdc.current, camera);
    const hit = raycaster.intersectObject(clayRef.current.mesh, false)[0];
    if (!hit) {
      cursorRef.current?.hide();
      return;
    }
    // Show ring cursor at the hit
    const normal = hit.face
      ? hit.face.normal.clone().transformDirection(clayRef.current.mesh.matrixWorld)
      : new THREE.Vector3(0, 1, 0);
    cursorRef.current?.show(hit.point, normal);
    cursorRef.current?.setRadius(brushRef.current * 0.12);

    if (isDragging.current && hit.uv) {
      const now = performance.now();
      if (now - lastPaintTime.current > 8) {
        // ~120hz cap
        lastPaintTime.current = now;
        const u = hit.uv.x;
        const v = hit.uv.y;
        const cur = { u, v };
        // Brush radius in canvas px: map from world-brush-size and canvas resolution.
        const radiusPx = Math.max(
          8,
          brushRef.current * (isMobile ? 12 : 22),
        );
        // Per-frame strength — normalized so 60fps and 120fps feel the same.
        const dtFactor = Math.min(1, delta * 60);
        let strength: number;
        let tool: "shape" | "pull" | "smooth";
        if (toolRef.current === "smooth") {
          strength = 0.32 * dtFactor;
          tool = "smooth";
        } else if (toolRef.current === "pull") {
          strength = 0.22 * dtFactor;
          tool = "pull";
        } else {
          strength = 0.22 * dtFactor;
          tool = "shape";
        }
        dispEngine.paintStroke(lastPaintUvRef.current, cur, radiusPx, strength, tool);
        lastPaintUvRef.current = cur;
      }
    } else if (!isDragging.current) {
      lastPaintUvRef.current = null;
    }
  });

  // Set scene bg + fog once
  useEffect(() => {
    scene.background = new THREE.Color(BG_COLOR);
    scene.fog = new THREE.Fog(BG_COLOR, 25, 60);
    return () => {
      scene.fog = null;
    };
  }, [scene]);

  // React to viewport size for camera aspect (fiber handles this, but just in case).
  useEffect(() => {
    (camera as THREE.PerspectiveCamera).aspect = size.width / size.height;
    (camera as THREE.PerspectiveCamera).updateProjectionMatrix();
  }, [size, camera]);

  return (
    <>
      {/* Warm key light — soft, high, slightly left. 3200K workshop bulb. */}
      <directionalLight
        position={[-4.5, 9, 4]}
        intensity={1.9}
        color={new THREE.Color("#FFCC94")}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-far={40}
        shadow-camera-left={-8}
        shadow-camera-right={8}
        shadow-camera-top={8}
        shadow-camera-bottom={-8}
        shadow-bias={-0.0004}
        shadow-normalBias={0.02}
        shadow-radius={6}
      />
      {/* Cool bounce from right — keeps the shadow side from going black */}
      <directionalLight position={[6, 3.5, 2.5]} intensity={0.4} color={new THREE.Color("#B8CCE8")} />
      {/* Warm rim from behind — defines silhouette without shine */}
      <spotLight
        position={[-2, 4, -5]}
        intensity={1.1}
        color={new THREE.Color("#FF9C60")}
        angle={Math.PI / 5}
        penumbra={0.85}
        distance={18}
      />
      {/* Ambient warm bounce — unfired clay eats a lot of light diffusely */}
      <ambientLight intensity={0.28} color={new THREE.Color("#EFCFAB")} />

      {/* Very low env intensity — unfired clay barely reflects the environment. */}
      <Environment preset="apartment" environmentIntensity={0.18} />

      <ContactShadows position={[0, -0.29, 0]} opacity={0.55} scale={12} blur={2.5} far={4.5} resolution={512} color="#000000" />

      <Wheel speedRef={speedRef} reducedMotion={reducedMotion} />

      <Clay
        ref={clayRef}
        displacementMap={dispEngine.texture}
        color={clayColor}
        isMobile={isMobile}
        reducedMotion={reducedMotion}
        speedRef={speedRef}
      />

      <RingCursor ref={cursorRef} reducedMotion={reducedMotion} />
    </>
  );
}